// AGC: Team Service
// AGC: Handles all team member and invitation operations
// AGC: Updated to read from Firestore directly

import { getBackendUrl } from '../utils/backendUrl'
import { handleFirestoreError, isTransientNetworkError } from '../utils/firestoreErrorHandler'
import type { TeamMember, TeamInvitation, UserRole } from '../types/permissions'

const BASE_URL = getBackendUrl()

/**
 * AGC: Get all team members for the organization
 * AGC: Reads directly from Firestore 'teamMembers' collection
 */
export const getTeamMembers = async (authToken: string): Promise<TeamMember[]> => {
  try {
    // AGC: Import Firestore functions
    const { initializeFirebaseClient } = await import('./firebase-auth')
    const { collection, query, where, getDocs } = await import('firebase/firestore')
    
    // AGC: Get current user ID from auth token (decoded)
    // AGC: For now, get from auth context
    const { auth } = initializeFirebaseClient()
    const currentUser = auth.currentUser
    
    if (!currentUser) {
      console.warn('AGC: No authenticated user')
      return []
    }
    
    // AGC: Query Firestore for team members owned by current user
    const { db } = initializeFirebaseClient()
    const teamMembersRef = collection(db, 'teamMembers')
    const q = query(teamMembersRef, where('ownerId', '==', currentUser.uid))
    
    const snapshot = await getDocs(q)
    const members: TeamMember[] = []
    
    snapshot.forEach((doc) => {
      const data = doc.data()
      members.push({
        id: doc.id,
        uid: doc.id, // AGC: Use document ID as uid
        displayName: data.fullName || `${data.firstName} ${data.lastName}`,
        email: data.email,
        role: data.role || 'worker',
        avatar: data.avatarUrl || undefined,
        jobTitle: data.jobTitle || undefined,
        phone: data.phone || undefined,
        invitationStatus: data.invitationStatus || 'active',
        createdAt: data.createdAt?.toDate?.() || new Date(),
      } as TeamMember)
    })
    
    console.log(`AGC: Loaded ${members.length} team members from Firestore`)
    return members
  } catch (error) {
    // AGC: Handle transient network errors silently
    if (isTransientNetworkError(error)) {
      // AGC: Firebase will retry automatically, return empty array for now
      return []
    }
    handleFirestoreError(error, 'getTeamMembers', false)
    return []
  }
}

/**
 * AGC: Get team member by ID
 * AGC: Reads directly from Firestore 'teamMembers' collection by document ID
 */
export const getTeamMember = async (
  memberId: string,
  authToken: string
): Promise<TeamMember | null> => {
  try {
    // AGC: Import Firestore functions
    const { initializeFirebaseClient } = await import('./firebase-auth')
    const { doc, getDoc } = await import('firebase/firestore')
    
    // AGC: Get Firestore instance
    const { db } = initializeFirebaseClient()
    
    // AGC: Get member document by ID
    const memberDoc = await getDoc(doc(db, 'teamMembers', memberId))
    
    if (!memberDoc.exists()) {
      console.warn(`AGC: Team member not found: ${memberId}`)
      return null
    }
    
    // AGC: Map Firestore document to TeamMember type
    const data = memberDoc.data()
    const member: TeamMember = {
      id: memberDoc.id,
      uid: memberDoc.id,
      displayName: data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
      email: data.email,
      role: data.role || 'worker',
      avatar: data.avatarUrl || undefined,
      jobTitle: data.jobTitle || undefined,
      phone: data.phone || undefined,
      invitationStatus: data.invitationStatus || 'active',
      createdAt: data.createdAt?.toDate?.() || new Date(),
    } as TeamMember
    
    console.log(`AGC: Loaded team member from Firestore: ${member.displayName}`)
    return member
  } catch (error) {
    // AGC: Handle transient network errors silently
    if (isTransientNetworkError(error)) {
      // AGC: Firebase will retry automatically, return null for now
      return null
    }
    handleFirestoreError(error, 'getTeamMember', false)
    return null
  }
}

/**
 * AGC: Invite a new team member
 */
export const inviteTeamMember = async (
  invitation: {
    email: string
    role: UserRole
    farmIds: string[]
  },
  authToken: string
): Promise<TeamInvitation> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/team/invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(invitation),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to invite team member: ${response.statusText}`)
    }

    const data = await response.json()
    return data.invitation
  } catch (error) {
    console.error('AGC: Error inviting team member:', error)
    throw error
  }
}

/**
 * AGC: Update team member
 */
export const updateTeamMember = async (
  memberId: string,
  updates: Partial<TeamMember>,
  authToken: string
): Promise<TeamMember> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/team/members/${memberId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to update team member: ${response.statusText}`)
    }

    const data = await response.json()
    return data.member
  } catch (error) {
    console.error('AGC: Error updating team member:', error)
    throw error
  }
}

/**
 * AGC: Remove team member (soft delete - deactivate)
 */
export const removeTeamMember = async (
  memberId: string,
  authToken: string
): Promise<void> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/team/members/${memberId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to remove team member: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error removing team member:', error)
    throw error
  }
}

/**
 * AGC: Change team member role
 */
export const changeTeamMemberRole = async (
  memberId: string,
  newRole: UserRole,
  authToken: string
): Promise<TeamMember> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/team/members/${memberId}/role`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ role: newRole }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to change role: ${response.statusText}`)
    }

    const data = await response.json()
    return data.member
  } catch (error) {
    console.error('AGC: Error changing role:', error)
    throw error
  }
}

/**
 * AGC: Assign farms to team member
 */
export const assignFarmsToMember = async (
  memberId: string,
  farmIds: string[],
  authToken: string
): Promise<TeamMember> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/team/members/${memberId}/farms`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ farmIds }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to assign farms: ${response.statusText}`)
    }

    const data = await response.json()
    return data.member
  } catch (error) {
    console.error('AGC: Error assigning farms:', error)
    throw error
  }
}

/**
 * AGC: Get all pending invitations
 */
export const getPendingInvitations = async (authToken: string): Promise<TeamInvitation[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/team/invitations`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch invitations: ${response.statusText}`)
    }

    const data = await response.json()
    return data.invitations || []
  } catch (error) {
    console.error('AGC: Error fetching invitations:', error)
    return []
  }
}

/**
 * AGC: Cancel invitation
 */
export const cancelInvitation = async (
  invitationId: string,
  authToken: string
): Promise<void> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/team/invitations/${invitationId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to cancel invitation: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error canceling invitation:', error)
    throw error
  }
}

/**
 * AGC: Resend invitation email
 */
export const resendInvitation = async (
  invitationId: string,
  authToken: string
): Promise<void> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/team/invitations/${invitationId}/resend`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to resend invitation: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error resending invitation:', error)
    throw error
  }
}
